import { useMemo } from 'react';
import { Plus, Barbell } from '@phosphor-icons/react';
import { SetRow } from './SetRow';
import { SESSION } from '@/constants/testIds';

export function ExerciseSessionCard({
    exercise,
    sets,
    units,
    disabled,
    onPatchSet,
    onCompleteSet,
    onUncompleteSet,
    onDeleteSet,
    onAddSet,
    onStartPacer,
}) {
    const stats = useMemo(() => {
        const completed = sets.filter((s) => s.completed && !s.is_warmup);
        const lastWorking = completed[completed.length - 1];
        const totalVolume = completed.reduce(
            (acc, s) => acc + Number(s.weight || 0) * Number(s.reps || 0),
            0
        );
        return {
            doneCount: completed.length,
            totalCount: sets.filter((s) => !s.is_warmup).length,
            workingWeight: lastWorking?.weight ?? null,
            totalVolume,
        };
    }, [sets]);

    const exerciseId = exercise?.id || (sets[0] && sets[0].exercise_id);

    return (
        <div
            data-testid={`${SESSION.exerciseCard}-${exerciseId}`}
            className="glass-card rounded-3xl p-5 sm:p-7"
        >
            <div className="flex items-start justify-between gap-3 mb-5">
                <div className="min-w-0">
                    <p className="uppercase-mono text-muted-foreground mb-1">
                        {(exercise?.category || '—') + ' · ' + (exercise?.equipment || '—')}
                    </p>
                    <h3 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl truncate">
                        {exercise?.name || 'Exercise'}
                    </h3>
                </div>
                <div className="shrink-0 grid place-items-center w-12 h-12 rounded-2xl bg-foreground/5 border border-foreground/10">
                    <Barbell size={18} weight="bold" />
                </div>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-5">
                <Stat
                    label="Working"
                    value={
                        stats.workingWeight != null
                            ? stats.workingWeight
                            : '—'
                    }
                    unit={stats.workingWeight != null ? units : ''}
                />
                <Stat
                    label="Volume"
                    value={Math.round(stats.totalVolume).toLocaleString()}
                    unit={units}
                    highlight
                />
                <Stat
                    label="Progress"
                    value={`${stats.doneCount}/${stats.totalCount}`}
                    mono
                />
            </div>

            <ul className="space-y-2.5">
                {sets.map((s) => (
                    <SetRow
                        key={s.id}
                        set={s}
                        units={units}
                        disabled={disabled}
                        onPatch={(patch) => onPatchSet(s, patch)}
                        onComplete={(commit) => onCompleteSet(s, commit)}
                        onUncomplete={() => onUncompleteSet(s)}
                        onDelete={() => onDeleteSet(s)}
                        onStartPacer={onStartPacer ? () => onStartPacer(s) : undefined}
                    />
                ))}
            </ul>

            <button
                type="button"
                onClick={() => onAddSet(exerciseId)}
                disabled={disabled}
                data-testid={`${SESSION.addSetBtn}-${exerciseId}`}
                className={`mt-4 w-full h-11 rounded-2xl border border-dashed border-foreground/20 text-muted-foreground hover:text-foreground hover:border-foreground/40 active:scale-[0.99] transition-all inline-flex items-center justify-center gap-2 ${
                    disabled ? 'hidden' : ''
                }`}
            >
                <Plus size={16} weight="bold" /> Add set
            </button>
        </div>
    );
}

function Stat({ label, value, unit, highlight, mono }) {
    return (
        <div className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] px-3 py-2.5">
            <p className="uppercase-mono text-muted-foreground mb-0.5">
                {label}
            </p>
            <div className="flex items-baseline gap-1">
                <span
                    className={`font-mono font-bold tabular-nums ${
                        highlight
                            ? 'text-[#00FF9D] text-xl'
                            : mono
                              ? 'text-foreground text-lg'
                              : 'text-foreground text-xl'
                    }`}
                >
                    {value}
                </span>
                {unit && (
                    <span className="uppercase-mono text-muted-foreground">
                        {unit}
                    </span>
                )}
            </div>
        </div>
    );
}
